import ReactECharts from "echarts-for-react";
import { useMemo } from "react";
import { baseOption, chartTokens, hexA, tooltipHtml } from "@/lib/chartTheme";
import { prepareLineTuples } from "@/lib/chartGaps";
import { useThemeVersion } from "@/hooks/useThemeVersion";
import { formatDateTime } from "@/lib/format";
import type { SiteSnapshot } from "@/data/types";

/**
 * Dual-axis BESS chart — active power on the left, SOC on the right.
 *
 * Sign convention: positive power = charging into the battery, negative
 * = discharging. Pass `bessId` to scope to one unit; omit for the fleet
 * aggregate (sum of power, average of SOC).
 *
 * Includes mouse-wheel + slider zoom so the operator can drill into any
 * sub-window of the 60-minute buffer.
 */
export function BessSocPowerChart({
  snapshots,
  bessId,
  height = 240,
}: {
  snapshots: SiteSnapshot[];
  bessId?: string;
  height?: number;
}) {
  // Bumps when [data-theme] flips so chartTokens() are re-read and the
  // option repaints in the new theme's colours (DSGN-07).
  const themeVersion = useThemeVersion();
  const option = useMemo(() => {
    const base = baseOption();
    const t = chartTokens();
    const soc: [number, number][] = [];
    const power: [number, number][] = [];
    for (const s of snapshots) {
      if (bessId) {
        const u = s.bess_per_unit.find((x) => x.id === bessId);
        if (!u) continue;
        soc.push([s.ts, u.soc_pct]);
        power.push([s.ts, u.active_W / 1000]);
      } else {
        const units = s.bess_per_unit;
        if (!units.length) continue;
        const totalP = units.reduce((a, x) => a + x.active_W, 0);
        const avgSoc = units.reduce((a, x) => a + x.soc_pct, 0) / units.length;
        soc.push([s.ts, avgSoc]);
        power.push([s.ts, totalP / 1000]);
      }
    }
    return {
      ...base,
      grid: { ...base.grid, top: 32, left: 56, right: 56, bottom: 44, containLabel: true },
      xAxis: { ...(base.xAxis as object), type: "time" },
      // No axis names — they collided with the top-right legend (WALK-06).
      // The unit rides on every tick label instead (DSGN-09).
      yAxis: [
        {
          type: "value",
          scale: true,
          axisLabel: { color: t.textMuted, fontSize: 11, formatter: (v: number) => `${v.toFixed(0)} kW` },
          splitLine: { lineStyle: { color: t.grid, type: "dashed" } },
        },
        {
          type: "value",
          position: "right",
          min: 0,
          max: 100,
          axisLabel: { color: t.textMuted, fontSize: 11, formatter: (v: number) => `${v.toFixed(0)}%` },
          splitLine: { show: false },
        },
      ],
      legend: { ...(base.legend as object), data: ["Active power", "State of charge"] },
      dataZoom: [
        { type: "inside", xAxisIndex: 0, filterMode: "none", zoomOnMouseWheel: true, moveOnMouseWheel: false, throttle: 50 },
        {
          type: "slider",
          xAxisIndex: 0,
          filterMode: "none",
          bottom: 4,
          height: 18,
          showDetail: false,
          borderColor: t.tooltipBorder,
          backgroundColor: hexA(t.textFaint, 0.06),
          fillerColor: hexA(t.accent, 0.12),
          handleStyle: { color: t.accent, borderColor: t.accent },
          moveHandleStyle: { color: t.accent },
          dataBackground: {
            lineStyle: { color: hexA(t.charge, 0.5), width: 1 },
            areaStyle: { color: hexA(t.charge, 0.10) },
          },
          selectedDataBackground: {
            lineStyle: { color: t.accent, width: 1 },
            areaStyle: { color: hexA(t.accent, 0.18) },
          },
        },
      ],
      series: [
        {
          name: "Active power",
          type: "line",
          showSymbol: false,
          smooth: 0.28,
          connectNulls: false,
          data: prepareLineTuples(power),
          lineStyle: { color: t.charge, width: 2.4 },
          itemStyle: { color: t.charge }, // legend marker — avoid palette fallback
          areaStyle: {
            opacity: 1,
            color: {
              type: "linear", x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0,    color: hexA(t.charge, 0.32) },
                { offset: 0.45, color: hexA(t.charge, 0.14) },
                { offset: 1,    color: hexA(t.charge, 0)    },
              ],
            },
          },
          z: 2,
        },
        {
          // SOC is continuous physical state, not a step signal — draw
          // a smooth line so the operator reads the trajectory rather
          // than discrete jumps at sample boundaries.
          name: "State of charge",
          type: "line",
          showSymbol: false,
          smooth: 0.25,
          connectNulls: false,
          yAxisIndex: 1,
          data: prepareLineTuples(soc),
          // Brand blue, NOT warn — amber is the SPA's degraded-state
          // vocabulary and must never paint a healthy data series (DSGN-08).
          lineStyle: { color: t.brand, width: 1.8 },
          itemStyle: { color: t.brand },
          z: 3,
        },
      ],
      tooltip: {
        ...(base.tooltip as object),
        // DSGN-09: unit dispatched per series (kW vs %) through the shared
        // tooltipHtml() card used by the other charts.
        formatter: (params: Array<{ axisValue: number; seriesName: string; color: string; value: [number, number | null] }>) => {
          const list = Array.isArray(params) ? params : [params];
          if (!list.length) return "";
          return tooltipHtml({
            header: formatDateTime(Number(list[0].axisValue)),
            rows: list.map((p) => {
              const raw = Array.isArray(p.value) ? p.value[1] : null;
              const ok = typeof raw === "number" && Number.isFinite(raw);
              const isSoc = p.seriesName === "State of charge";
              return {
                color: String(p.color),
                label: p.seriesName,
                value: ok ? raw.toFixed(1) : "—",
                unit: ok ? (isSoc ? "%" : "kW") : undefined,
              };
            }),
          });
        },
      },
    } as object;
  }, [snapshots, bessId, themeVersion]);
  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate />;
}

/** Summary metrics for the header chip beside the chart. */
export function bessChartSummary(snapshots: SiteSnapshot[], bessId?: string) {
  if (!snapshots.length) return { soc_now: 0, soc_min: 0, soc_max: 0, p_min_kW: 0, p_max_kW: 0, p_avg_kW: 0 };
  let socNow = 0;
  let socMin = 100;
  let socMax = 0;
  let pMin = Infinity;
  let pMax = -Infinity;
  let pSum = 0;
  let n = 0;
  for (const s of snapshots) {
    const u = bessId ? s.bess_per_unit.find((x) => x.id === bessId) : null;
    const soc = u ? u.soc_pct : (s.bess_per_unit.length ? s.bess_per_unit.reduce((a, x) => a + x.soc_pct, 0) / s.bess_per_unit.length : 0);
    const p = (u ? u.active_W : s.bess_per_unit.reduce((a, x) => a + x.active_W, 0)) / 1000;
    socNow = soc;
    if (soc < socMin) socMin = soc;
    if (soc > socMax) socMax = soc;
    if (p < pMin) pMin = p;
    if (p > pMax) pMax = p;
    pSum += p;
    n++;
  }
  return {
    soc_now: socNow,
    soc_min: Number.isFinite(socMin) ? socMin : 0,
    soc_max: Number.isFinite(socMax) ? socMax : 0,
    p_min_kW: Number.isFinite(pMin) ? pMin : 0,
    p_max_kW: Number.isFinite(pMax) ? pMax : 0,
    p_avg_kW: n ? pSum / n : 0,
  };
}
